import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus } from 'lucide-react';
import { companyAPI, masterDataAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const TAB_DOCTYPE = { companies: 'company', 'company-codes': 'company_code' };

const Company = () => {
  const { canWrite } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'companies';
  const [companies, setCompanies] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Master data for dropdowns
  const [currencies, setCurrencies] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    setActiveTab(tabFromUrl === 'company-codes' ? 'company-codes' : 'companies');
  }, [tabFromUrl]);

  useEffect(() => {
    loadData();
    loadMasterData();
  }, []);

  const loadData = async () => {
    try {
      const [comps, codes] = await Promise.all([
        companyAPI.getCompanies(),
        companyAPI.getCompanyCodes(),
      ]);
      setCompanies(comps.data);
      setCompanyCodes(codes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const loadMasterData = async () => {
    try {
      const [curr, lang, ctry] = await Promise.all([
        masterDataAPI.getCurrencies(),
        masterDataAPI.getLanguages(),
        masterDataAPI.getCountries(),
      ]);
      setCurrencies(curr.data);
      setLanguages(lang.data);
      setCountries(ctry.data);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {};
    formData.forEach((value, key) => {
      data[key] = value;
    });

    try {
      if (activeTab === 'companies') {
        if (editingItem) {
          await companyAPI.updateCompany(editingItem.company_id, data);
        } else {
          await companyAPI.createCompany(data);
        }
      } else {
        if (editingItem) {
          await companyAPI.updateCompanyCode(editingItem.company_code_id, data);
        } else {
          await companyAPI.createCompanyCode(data);
        }
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  // Delete functionality removed as per requirements

  const CompanyForm = () => {
    const [selectedCurrency, setSelectedCurrency] = useState(editingItem?.currency || '');
    const [selectedLanguage, setSelectedLanguage] = useState(editingItem?.language || '');
    const [selectedCountry, setSelectedCountry] = useState(editingItem?.country || '');

    return (
      <React.Fragment>
        <div>
          <Label>Company ID</Label>
          <Input 
            name="company_id" 
            defaultValue={editingItem?.company_id} 
            required 
            disabled={!!editingItem} 
            placeholder="e.g., C001"
            onChange={(e) => e.target.value = e.target.value.toUpperCase()}
            style={{ textTransform: 'uppercase' }}
          />
        </div>
        <div>
          <Label>Company Name</Label>
          <Input name="company_name" defaultValue={editingItem?.company_name} required placeholder="e.g., Acme Corporation" />
        </div>
        <div>
          <Label>Street</Label>
          <Input name="street" defaultValue={editingItem?.street} required placeholder="e.g., 123 Main St" />
        </div>
        <div>
          <Label>PO Box</Label>
          <Input name="po_box" defaultValue={editingItem?.po_box} required placeholder="e.g., PO Box 123" />
        </div>
        <div>
          <Label>Postal Code</Label>
          <Input name="postal_code" defaultValue={editingItem?.postal_code} required placeholder="e.g., 12345" />
        </div>
        <div>
          <Label>City</Label>
          <Input name="city" defaultValue={editingItem?.city} required placeholder="e.g., New York" />
        </div>
        <div>
          <Label>Currency</Label>
          <Select name="currency" value={selectedCurrency} onValueChange={setSelectedCurrency} required>
            <SelectTrigger>
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {currencies.map(curr => (
                <SelectItem key={curr.currency_code} value={curr.currency_code}>
                  {curr.currency_code} - {curr.currency_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="currency" value={selectedCurrency} />
        </div>
        <div>
          <Label>Language</Label>
          <Select name="language" value={selectedLanguage} onValueChange={setSelectedLanguage} required>
            <SelectTrigger>
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map(lang => (
                <SelectItem key={lang.language_code} value={lang.language_code}>
                  {lang.language_code} - {lang.language_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="language" value={selectedLanguage} />
        </div>
        <div>
          <Label>Country</Label>
          <Select name="country" value={selectedCountry} onValueChange={setSelectedCountry} required>
            <SelectTrigger>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map(ctry => (
                <SelectItem key={ctry.country_code} value={ctry.country_code}>
                  {ctry.country_code} - {ctry.country_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="country" value={selectedCountry} />
        </div>
      </React.Fragment>
    );
  };

  const CompanyCodeForm = () => {
    const [selectedCompany, setSelectedCompany] = useState(editingItem?.company_id || '');
    const [selectedCountry, setSelectedCountry] = useState(editingItem?.country_code || '');
    const [selectedCurrency, setSelectedCurrency] = useState(editingItem?.currency_code || '');
    const [selectedLanguage, setSelectedLanguage] = useState(editingItem?.language_code || '');

    return (
      <React.Fragment>
        <div>
          <Label>Company Code ID</Label>
          <Input 
            name="company_code_id" 
            defaultValue={editingItem?.company_code_id} 
            required 
            disabled={!!editingItem} 
            placeholder="e.g., CC001"
            onChange={(e) => e.target.value = e.target.value.toUpperCase()}
            style={{ textTransform: 'uppercase' }}
          />
        </div>
        <div>
          <Label>Company Code Name</Label>
          <Input name="company_code_name" defaultValue={editingItem?.company_code_name} required placeholder="e.g., Acme US Operations" />
        </div>
        <div>
          <Label>City</Label>
          <Input name="city" defaultValue={editingItem?.city} required placeholder="e.g., New York" />
        </div>
        <div>
          <Label>Company</Label>
          {companies.length === 0 ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
              Please create a Company first before creating Company Codes
            </div>
          ) : (
            <React.Fragment>
              <Select value={selectedCompany} onValueChange={setSelectedCompany} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(comp => (
                    <SelectItem key={comp.company_id} value={comp.company_id}>
                      {comp.company_id} - {comp.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="company_id" value={selectedCompany} />
            </React.Fragment>
          )}
        </div>
        <div>
          <Label>Country</Label>
          <Select value={selectedCountry} onValueChange={setSelectedCountry} required>
            <SelectTrigger>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map(country => (
                <SelectItem key={country.country_code} value={country.country_code}>
                  {country.country_code} - {country.country_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="country_code" value={selectedCountry} />
        </div>
        <div>
          <Label>Currency</Label>
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency} required>
            <SelectTrigger>
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {currencies.map(currency => (
                <SelectItem key={currency.currency_code} value={currency.currency_code}>
                  {currency.currency_code} - {currency.currency_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="currency_code" value={selectedCurrency} />
        </div>
        <div>
          <Label>Language</Label>
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage} required>
            <SelectTrigger>
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map(language => (
                <SelectItem key={language.language_code} value={language.language_code}>
                  {language.language_code} - {language.language_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="language_code" value={selectedLanguage} />
        </div>
      </React.Fragment>
    );
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="company-page">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Company Management</h2>
          <p className="text-sm text-gray-500 mt-1">Manage companies and company codes</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex">
            <button
              type="button"
              onClick={() => {
                setActiveTab('companies');
                setEditingItem(null);
                setIsDialogOpen(false);
              }}
              className={`px-6 py-4 font-medium ${
                activeTab === 'companies'
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600'
              }`}
              data-testid="tab-companies"
            >
              Companies
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('company-codes');
                setEditingItem(null);
                setIsDialogOpen(false);
              }}
              className={`px-6 py-4 font-medium ${
                activeTab === 'company-codes'
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600'
              }`}
              data-testid="tab-company-codes"
            >
              Company Codes
            </button>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {activeTab === 'companies' ? 'Companies' : 'Company Codes'}
            </h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              {canWrite(TAB_DOCTYPE[activeTab]) && (
                <DialogTrigger asChild>
                  <Button 
                    type="button"
                    onClick={() => setEditingItem(null)} 
                    data-testid="btn-add-new"
                    disabled={activeTab === 'company-codes' && companies.length === 0}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className="max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} {activeTab === 'companies' ? 'Company' : 'Company Code'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {activeTab === 'companies' ? <CompanyForm /> : <CompanyCodeForm />}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1" data-testid="btn-submit-form">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingItem(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto" key={activeTab}>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">City</th>
                  {activeTab === 'company-codes' && (
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company</th>
                  )}
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'companies' ? companies : companyCodes).length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'company-codes' ? 5 : 4} className="text-center py-8 text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  (activeTab === 'companies' ? companies : companyCodes).map((item) => (
                    <tr key={item.company_id || item.company_code_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">
                        {activeTab === 'companies' ? item.company_id : item.company_code_id}
                      </td>
                      <td className="py-3 px-4 text-sm">{item.company_name || item.company_code_name}</td>
                      <td className="py-3 px-4 text-sm">{item.city}</td>
                      {activeTab === 'company-codes' && (
                        <td className="py-3 px-4 text-sm">
                          <div className="text-xs text-gray-500">Company: {item.company_id}</div>
                        </td>
                      )}
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <RecordActions
                            doctype={TAB_DOCTYPE[activeTab]}
                            idForTestId={item.company_id || item.company_code_id}
                            onView={() => setViewItem(item)}
                            onEdit={() => { setEditingItem(item); setIsDialogOpen(true); }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title={activeTab === 'companies' ? 'Company Details' : 'Company Code Details'}
        record={viewItem}
      />
    </Layout>
  );
};

export default Company;
